import { create } from "zustand";
import {
  activateFinance,
  getFinanceStatus,
} from "../services/financeService";
import type { Funnel } from "../components/dashboard/funnels/types";

interface FinanceState {
  onboardingCompleted: boolean;
  financeActivatedAt: string | null;
  metaConnected: boolean;

  statusLoading: boolean;
  statusError: string | null;

  activating: boolean;
  activationError: string | null;

  dropiChoice: "yes" | "no" | null;
  dropiConnected: boolean;

  funnels: Funnel[];

  fetchStatus: () => Promise<void>;
  completeOnboarding: () => Promise<void>;
  resetOnboarding: () => void;
  previewOnboarding: () => void;
  setMetaConnected: (connected: boolean) => void;
  setDropiChoice: (choice: "yes" | "no") => void;
  setDropiConnected: (connected: boolean) => void;
  setFunnels: (funnels: Funnel[]) => void;
  updateFunnelMatch: (funnelId: string, campaignIds: string[]) => void;
}

export const useFinanceStore = create<FinanceState>((set) => ({
  onboardingCompleted: false,
  financeActivatedAt: null,
  metaConnected: false,
  statusLoading: false,
  statusError: null,
  activating: false,
  activationError: null,
  dropiChoice: null,
  dropiConnected: false,
  funnels: [],

  fetchStatus: async () => {
    set({ statusLoading: true, statusError: null });
    try {
      const response = await getFinanceStatus();
      const readyForFinance = Boolean(response.finance_enabled && response.meta_connected);
      if (!readyForFinance) {
        sessionStorage.removeItem("finance_active_tab");
      }
      set({
        onboardingCompleted: readyForFinance,
        financeActivatedAt: response.finance_activated_at ?? null,
        metaConnected: Boolean(response.meta_connected),
        statusLoading: false,
        statusError: null,
      });
    } catch (error) {
      set({
        statusLoading: false,
        statusError:
          error instanceof Error
            ? error.message
            : "Error al consultar el estado de Finance",
      });
    }
  },

  completeOnboarding: async () => {
    set({ activating: true, activationError: null });
    try {
      const response = await activateFinance();
      set({
        onboardingCompleted: Boolean(response.finance_enabled && response.meta_connected),
        financeActivatedAt: response.finance_activated_at ?? null,
        metaConnected: Boolean(response.meta_connected),
        activating: false,
        activationError: null,
      });
    } catch (error) {
      set({
        activating: false,
        activationError:
          error instanceof Error ? error.message : "Error al activar Finance",
      });
      throw error;
    }
  },

  resetOnboarding: () => {
    sessionStorage.removeItem("finance_active_tab");
    set({
      onboardingCompleted: false,
      financeActivatedAt: null,
      metaConnected: false,
      dropiChoice: null,
      dropiConnected: false,
      funnels: [],
      activationError: null,
      statusError: null,
    });
  },

  previewOnboarding: () => {
    sessionStorage.removeItem("finance_active_tab");
  },

  setMetaConnected: (connected) => set({ metaConnected: connected }),
  setDropiChoice: (choice) =>
    set((state) => ({
      dropiChoice: choice,
      dropiConnected: choice === "no" ? false : state.dropiConnected,
    })),
  setDropiConnected: (connected) => set({ dropiConnected: connected }),
  setFunnels: (funnels) => set({ funnels }),
  updateFunnelMatch: (funnelId, campaignIds) =>
    set((state) => ({
      funnels: state.funnels.map((funnel) =>
        funnel.id === funnelId
          ? {
              ...funnel,
              metaStatus: "configured_meta",
              matchedCampaignIds: campaignIds,
            }
          : funnel
      ),
    })),
}));
